import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { syncAPI } from '../lib/api';
import { CheckCircle2, AlertCircle, Loader2, Clock, XCircle, Globe } from 'lucide-react';

function formatRelativeTime(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'hace un momento';
  if (diffMins < 60) return `hace ${diffMins} min`;
  if (diffHours < 24) return `hace ${diffHours}h`;
  if (diffDays === 1) return 'ayer';
  return `hace ${diffDays} días`;
}

export default function ArcGISSyncStatus({ isSyncing = false }) {
  const { data: syncStatus } = useQuery({
    queryKey: ['arcgis-sync-status'],
    queryFn: () => syncAPI.getArcGISSyncStatus().then(r => r.data),
    refetchInterval: 30000,
    retry: false,
  });

  // Determine display state
  const isNeverSynced = !syncStatus || syncStatus.status === 'never_synced';
  const isInProgress = isSyncing || syncStatus?.status === 'in_progress';
  const isFailed = syncStatus?.status === 'failed' || (syncStatus?.failed > 0 && syncStatus?.added === 0 && syncStatus?.updated === 0);
  const isWarning = syncStatus?.status === 'completed' && syncStatus?.failed > 0;
  const isOk = syncStatus?.status === 'completed' && !isWarning;

  const lastSyncTime = syncStatus?.completed_at || syncStatus?.started_at;
  const relativeTime = formatRelativeTime(lastSyncTime);
  const total = (syncStatus?.added || 0) + (syncStatus?.updated || 0);

  if (isNeverSynced && !isSyncing) return null;

  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
        isInProgress
          ? 'bg-blue-50 border-blue-200 text-blue-700'
          : isFailed
          ? 'bg-red-50 border-red-200 text-red-700'
          : isWarning
          ? 'bg-orange-50 border-orange-200 text-orange-700'
          : 'bg-emerald-50 border-emerald-200 text-emerald-700'
      }`}
      title={syncStatus?.message || ''}
      data-testid="arcgis-sync-status"
    >
      {isInProgress ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : isFailed ? (
        <XCircle className="w-3 h-3" />
      ) : isWarning ? (
        <AlertCircle className="w-3 h-3" />
      ) : (
        <CheckCircle2 className="w-3 h-3" />
      )}

      <span>
        {isInProgress
          ? 'Sincronizando...'
          : isFailed
          ? `Error · ${relativeTime}`
          : isWarning
          ? `${total} sync · ${syncStatus.failed} errores · ${relativeTime}`
          : `${total} registros · ${relativeTime}`}
      </span>
    </div>
  );
}
